import type { ResolvedPageHeroCopy } from './types';

type ResolvedPageCopy = {
  more: ResolvedPageHeroCopy;
  home: { defaultEstateLine: string; nameSuffix: string };
  facilities: ResolvedPageHeroCopy & {
    searchPlaceholder: string;
    myBookings: ResolvedPageHeroCopy;
    detail: { chooseDate: string; availableTimes: string };
  };
  documents: ResolvedPageHeroCopy;
  events: ResolvedPageHeroCopy & { emptyTitle: string; emptyDescription: string };
  gallery: ResolvedPageHeroCopy;
  newsStand: ResolvedPageHeroCopy;
  suppliers: ResolvedPageHeroCopy & { emptyTitle: string; emptyDescription: string };
  map: ResolvedPageHeroCopy & {
    locationsTitle: string;
    locationsDescription: string;
  };
  faq: ResolvedPageHeroCopy;
  classifieds: ResolvedPageHeroCopy & {
    searchPlaceholder: string;
    form: { new: ResolvedPageHeroCopy; edit: ResolvedPageHeroCopy };
  };
  support: {
    list: ResolvedPageHeroCopy;
    new: ResolvedPageHeroCopy;
    detailEyebrow: string;
  };
  profile: ResolvedPageHeroCopy & { accountBalance: string };
  properties: ResolvedPageHeroCopy & {
    sectionYourProperties: string;
    sectionPending: string;
    sectionNotApproved: string;
  };
  invoices: ResolvedPageHeroCopy & {
    accountBalance: string;
    balanceOwedTitle: string;
    balanceOwedHint: string;
    balanceSettledTitle: string;
    balanceSettledHint: string;
    balanceCreditLabel: string;
    payCta: string;
    statementsTitle: string;
    statementsHint: string;
  };
  panic: {
    sos: ResolvedPageHeroCopy;
    contacts: ResolvedPageHeroCopy;
    contactForm: { new: ResolvedPageHeroCopy; edit: ResolvedPageHeroCopy };
  };
  surveys: ResolvedPageHeroCopy;
  accessInvites: ResolvedPageHeroCopy;
  pets: ResolvedPageHeroCopy & {
    form: { new: ResolvedPageHeroCopy; edit: ResolvedPageHeroCopy };
  };
  vehicles: ResolvedPageHeroCopy & {
    form: { new: ResolvedPageHeroCopy; edit: ResolvedPageHeroCopy };
  };
  auth: {
    loginTitle: string;
    loginSubtitle: string;
    forgotEyebrow: string;
    forgotTitle: string;
    forgotSubtitle: string;
    forgotSentTitle: string;
    forgotSentSubtitle: string;
    signupEyebrow: string;
    signupTitle: string;
    signupOtpEyebrow: string;
    signupOtpTitle: string;
    signupOtpSubtitle: string;
    signupNotConfiguredTitle: string;
    signupNotConfiguredBody: string;
  };
  selectProperty: ResolvedPageHeroCopy;
  management: {
    emergencyLine: string;
    officeHours: string;
    team: string;
    emptyTitle: string;
    emptyDescription: string;
  };
  products: { searchLabel: string; emptyTitle: string; emptyDescription: string };
  settings: ResolvedPageHeroCopy & {
    display: string;
    security: string;
    notifications: string;
    about: string;
  };
  welcomeTour: Array<{ eyebrow: string; title: string; body: string }>;
};

export type { ResolvedPageCopy };

/** Built-in copy when a build env omits `pageCopy` overrides. */
export const DEFAULT_PAGE_COPY: ResolvedPageCopy = {
  more: {
    eyebrow: 'Estate',
    title: 'Explore',
    subtitle: 'Browse everything available in your estate.',
  },
  home: {
    defaultEstateLine: 'Your estate',
    nameSuffix: '.',
  },
  facilities: {
    eyebrow: 'Facilities',
    title: 'Book a facility.',
    subtitle: '',
    searchPlaceholder: 'Search facilities…',
    myBookings: {
      eyebrow: 'Book',
      title: 'My bookings',
      subtitle: 'View and manage your upcoming and past bookings.',
    },
    detail: {
      chooseDate: 'Choose date',
      availableTimes: 'Available times',
    },
  },
  documents: {
    eyebrow: 'Estate',
    title: 'Documents.',
    subtitle: 'Rules, notices and forms published by your community.',
  },
  events: {
    eyebrow: 'Community',
    title: 'Events.',
    subtitle: "What's on in your community this season.",
    emptyTitle: 'Nothing on yet.',
    emptyDescription: 'Events will appear here.',
  },
  gallery: {
    eyebrow: 'Gallery',
    title: 'Gallery.',
    subtitle: 'Photos from community events and life on the estate.',
  },
  newsStand: {
    eyebrow: 'Estate',
    title: 'News stand.',
    subtitle: 'Bulletins and updates published by your community.',
  },
  suppliers: {
    eyebrow: 'Local services',
    title: 'Suppliers.',
    subtitle: 'Browse approved suppliers by category.',
    emptyTitle: 'No suppliers.',
    emptyDescription: "Your community hasn't published any suppliers yet.",
  },
  map: {
    eyebrow: 'Estate',
    title: 'Map.',
    subtitle: 'Find amenities, gates, and key points on the estate.',
    locationsTitle: 'Locations',
    locationsDescription: 'Browse all published estate map points.',
  },
  faq: {
    eyebrow: 'Help',
    title: 'Frequently asked questions.',
    subtitle: 'Quick answers about levies, access, and estate procedures.',
  },
  classifieds: {
    eyebrow: 'Resident marketplace',
    title: 'Classifieds.',
    subtitle: 'Buy, sell, swap or share with your neighbours.',
    searchPlaceholder: 'Search listings…',
    form: {
      new: {
        eyebrow: 'Marketplace',
        title: 'New listing',
        subtitle: 'Share what you are selling, lending or giving away.',
      },
      edit: {
        eyebrow: 'Marketplace',
        title: 'Edit listing',
        subtitle: 'Update your listing details and photos.',
      },
    },
  },
  support: {
    list: {
      eyebrow: 'Support',
      title: 'My requests.',
      subtitle: 'Log issues, track progress and chat with the team.',
    },
    new: {
      eyebrow: 'New request',
      title: "Tell us what's\nhappening.",
      subtitle:
        'The right team will pick this up, usually within a few hours during business days.',
    },
    detailEyebrow: 'Support',
  },
  profile: {
    eyebrow: 'Account',
    title: 'Your profile.',
    subtitle: 'Your details, saved contacts and account balance.',
    accountBalance: 'Account balance',
  },
  properties: {
    eyebrow: 'Residence',
    title: 'My properties.',
    subtitle: 'Properties linked to your profile and claims awaiting approval.',
    sectionYourProperties: 'Your properties',
    sectionPending: 'Pending approval',
    sectionNotApproved: 'Not approved',
  },
  invoices: {
    eyebrow: 'Account',
    title: 'Account.',
    subtitle: 'Balance, invoices, payments and credit notes on your account.',
    accountBalance: 'Account balance',
    balanceOwedTitle: 'Payment due',
    balanceOwedHint: 'Please settle your outstanding balance to stay in good standing.',
    balanceSettledTitle: 'In good standing',
    balanceSettledHint: 'Your account is fully settled. Thank you.',
    balanceCreditLabel: 'in credit',
    payCta: 'Pay now',
    statementsTitle: 'Statements',
    statementsHint: 'Monthly statements will be available to download here soon.',
  },
  panic: {
    sos: {
      eyebrow: 'Emergency',
      title: 'Emergency',
      subtitle:
        'Quick access to estate emergency support, alert history, and the people who should be notified.',
    },
    contacts: {
      eyebrow: 'Safety',
      title: 'Emergency contacts',
      subtitle: 'People notified when you raise an SOS.',
    },
    contactForm: {
      new: {
        eyebrow: 'Safety',
        title: 'Add contact',
        subtitle: 'They will be alerted when you trigger an emergency.',
      },
      edit: {
        eyebrow: 'Safety',
        title: 'Edit contact',
        subtitle: 'Update how this person is reached during an SOS.',
      },
    },
  },
  surveys: {
    eyebrow: 'Estate',
    title: 'Surveys',
    subtitle: 'Fill in estate surveys and eForms, and review the responses you have already submitted.',
  },
  accessInvites: {
    eyebrow: 'Access',
    title: 'Visitor access',
    subtitle: 'Generate, send and track.',
  },
  pets: {
    eyebrow: 'Estate profile',
    title: 'Pets.',
    subtitle: 'Keep your household pets registered with the estate.',
    form: {
      new: {
        eyebrow: 'Estate profile',
        title: 'Add a pet',
        subtitle: 'These details help the estate identify pets when needed.',
      },
      edit: {
        eyebrow: 'Estate profile',
        title: 'Edit pet',
        subtitle: 'Update your pet’s details and photo.',
      },
    },
  },
  vehicles: {
    eyebrow: 'Estate profile',
    title: 'Vehicles.',
    subtitle: 'Registered vehicles.',
    form: {
      new: {
        eyebrow: 'Estate profile',
        title: 'Add a vehicle',
        subtitle: 'Add vehicle details.',
      },
      edit: {
        eyebrow: 'Estate profile',
        title: 'Edit vehicle',
        subtitle: 'Update vehicle details.',
      },
    },
  },
  auth: {
    loginTitle: 'Welcome back',
    loginSubtitle: 'Sign in to continue to your residence.',
    forgotEyebrow: 'Account recovery',
    forgotTitle: 'Forgot your\npassword?',
    forgotSubtitle:
      "No problem. Enter the email registered to your residence and we'll send you a secure reset link.",
    forgotSentTitle: 'Check your inbox.',
    forgotSentSubtitle:
      "If we found your account, we've sent a secure link to reset your password.",
    signupEyebrow: 'Create your account',
    signupTitle: 'A few details to begin.',
    signupOtpEyebrow: "Verify it's you",
    signupOtpTitle: 'Enter your code.',
    signupOtpSubtitle: 'We sent a one-time code to {email}. Enter it below.',
    signupNotConfiguredTitle: "This community isn't ready yet.",
    signupNotConfiguredBody:
      "Self-signup hasn't been set up for this estate yet. Please contact your estate manager or administrator.",
  },
  selectProperty: {
    eyebrow: 'Almost there',
    title: 'Select your residence.',
    subtitle:
      "We've matched these properties to your ID. Pick the one you'd like to manage first. You can switch later.",
  },
  management: {
    emergencyLine: 'Emergency line',
    officeHours: 'Office hours',
    team: 'Team',
    emptyTitle: 'No management info.',
    emptyDescription: "Your community hasn't shared management details yet.",
  },
  products: {
    searchLabel: 'Search products',
    emptyTitle: 'No products.',
    emptyDescription: 'Nothing matches that search.',
  },
  settings: {
    eyebrow: 'Account',
    title: 'Settings.',
    subtitle: 'Theme, security and app details.',
    display: 'Display',
    security: 'Security',
    notifications: 'Notifications',
    about: 'About',
  },
  welcomeTour: [
    {
      eyebrow: 'Welcome home',
      title: 'A warm welcome to {name}.',
      body: 'Your account is ready. Take a quick tour of what your residence app can do.',
    },
    {
      eyebrow: 'All in one place',
      title: 'Your community, at your fingertips.',
      body: 'Visitor access, facility bookings, statements and news, a tap away.',
    },
    {
      eyebrow: 'Always looked after',
      title: 'Help, whenever you need it.',
      body: 'Panic SOS, security contacts and resident support are always within reach.',
    },
  ],
};
